<template>
    <div class="star-rating"
         :class="{ 'is-disabled': disabled, 'has-value': selected !== null, 'is-compact': compact }"
         role="radiogroup"
         :aria-label="ariaLabel">
        <div class="star-rating__row" :style="{ '--sr-count': length }">
            <button v-for="n in length"
                    :key="n"
                    type="button"
                    class="star-rating__option"
                    :class="{ 'is-lit': n <= active, 'is-peak': n === active, 'is-selected': n === selected }"
                    role="radio"
                    :aria-checked="selected === n ? 'true' : 'false'"
                    :aria-label="labelFor(n)"
                    :tabindex="tabIndexFor(n)"
                    :disabled="disabled"
                    @click="select(n)"
                    @mouseenter="hovered = n"
                    @mouseleave="hovered = null"
                    @focus="hovered = n"
                    @blur="hovered = null"
                    @keydown.left.prevent="move(1)"
                    @keydown.right.prevent="move(-1)"
                    @keydown.up.prevent="move(-1)"
                    @keydown.down.prevent="move(1)"
                    @keydown.space.prevent="select(n)"
                    @keydown.enter.prevent="select(n)">
                <span class="star-rating__disc" aria-hidden="true">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="star-rating__svg">
                        <path class="star-rating__glyph" d="M12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.62L12 2 9.19 8.62 2 9.24l5.45 4.73L5.82 21z" />
                    </svg>
                </span>
                <span class="star-rating__num">{{ n }}</span>
            </button>
        </div>
        <div class="star-rating__caption" aria-live="polite">
            <transition name="star-rating-fade">
                <span v-if="active" :key="active">{{ captionFor(active) }}</span>
            </transition>
        </div>
    </div>
</template>

<script>
    /*
     * Star rating with cumulative fill.
     * v-model contract: emits the star count (1..length), same as the v-rating it replaces.
     */
    const WORDS = ['Terrible', 'Bad', 'Okay', 'Good', 'Great']

    export default {
        name: 'StarRating',
        props: {
            value: { type: [Number, String], default: null },
            length: { type: Number, default: 5 },
            disabled: { type: Boolean, default: false },
            compact: { type: Boolean, default: false },
            ariaLabel: { type: String, default: '' }
        },
        data() {
            return {
                hovered: null,
                selected: this.normalize(this.value)
            }
        },
        computed: {
            active() {
                return this.hovered || this.selected || 0
            }
        },
        watch: {
            value(val) {
                this.selected = this.normalize(val)
            }
        },
        methods: {
            normalize(val) {
                const n = Number(val)
                return n >= 1 && n <= this.length ? n : null
            },
            labelFor(n) {
                return this.length === 5 ? this.$t(WORDS[n - 1]) : `${n} / ${this.length}`
            },
            captionFor(n) {
                return this.length === 5 ? this.$t(WORDS[n - 1]) : `${n} / ${this.length}`
            },
            tabIndexFor(n) {
                if (this.selected !== null) return this.selected === n ? 0 : -1
                return n === 1 ? 0 : -1
            },
            select(n) {
                if (this.disabled) return
                this.selected = n
                this.$emit('input', n)
                this.$emit('change', n)
            },
            move(step) {
                if (this.disabled) return
                const current = this.selected || this.hovered || 1
                const next = Math.min(this.length, Math.max(1, current + step))
                this.select(next)
                this.$nextTick(() => {
                    const btn = this.$el.querySelectorAll('.star-rating__option')[next - 1]
                    if (btn) btn.focus()
                })
            }
        }
    }
</script>

<style scoped>
    .star-rating {
        --sr-surface: #f3f6f5;
        --sr-card: #ffffff;
        --sr-text: #1f2a28;
        --sr-muted: #6b7a77;
        --sr-ring: #0a716e;
        --sr-star: #eab308;
        --sr-star-deep: #a16207;
        --sr-star-tint: #fef3c7;
        --sr-idle: #c9d3d0;
        --sr-radius: 18px;
        --sr-option-radius: 14px;
        --sr-disc: 64px;
        --sr-glyph: 34px;
        --sr-ease: cubic-bezier(.2, .8, .2, 1);

        width: 100%;
        max-width: 600px;
        margin: 8px auto 0;
        direction: inherit;
    }

    .star-rating__row {
        display: grid;
        grid-template-columns: repeat(var(--sr-count, 5), minmax(0, 1fr));
        gap: 6px;
        padding: 8px;
        border-radius: var(--sr-radius);
        background: var(--sr-surface);
    }

    .star-rating__option {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 8px;
        padding: 14px 4px 10px;
        border: 0;
        border-radius: var(--sr-option-radius);
        background: transparent;
        cursor: pointer;
        font: inherit;
        color: var(--sr-muted);
        transition: background-color .22s var(--sr-ease), box-shadow .22s var(--sr-ease);
        -webkit-tap-highlight-color: transparent;
    }

    .star-rating__option:focus { outline: none; }
    .star-rating__option:focus-visible {
        box-shadow: 0 0 0 2px var(--sr-card), 0 0 0 4px var(--sr-ring);
    }

    .star-rating__disc {
        display: flex;
        align-items: center;
        justify-content: center;
        width: var(--sr-disc);
        height: var(--sr-disc);
        border-radius: 50%;
        background: var(--sr-card);
        box-shadow: 0 1px 2px rgba(15, 23, 42, .06), 0 4px 12px -4px rgba(15, 23, 42, .14);
        transition: background-color .22s var(--sr-ease), box-shadow .22s var(--sr-ease), transform .22s var(--sr-ease);
    }

    .star-rating__svg {
        width: var(--sr-glyph);
        height: var(--sr-glyph);
        display: block;
    }

    .star-rating__glyph {
        fill: var(--sr-idle);
        transition: fill .22s var(--sr-ease), transform .22s var(--sr-ease);
        transform-origin: center;
    }

    .star-rating__num {
        font-size: 12px;
        font-weight: 600;
        line-height: 1;
        color: var(--sr-muted);
        transition: color .22s var(--sr-ease);
    }

    /* Cumulative fill: every star up to the active one */
    .star-rating__option.is-lit .star-rating__disc {
        background: var(--sr-star-tint);
    }
    .star-rating__option.is-lit .star-rating__glyph {
        fill: var(--sr-star);
    }
    .star-rating__option.is-lit .star-rating__num {
        color: var(--sr-star-deep);
    }

    /* The active endpoint: solid disc, white star, card */
    .star-rating__option.is-peak {
        background: var(--sr-card);
        box-shadow: 0 1px 2px rgba(15, 23, 42, .05), 0 12px 28px -12px var(--sr-star);
    }
    .star-rating__option.is-peak .star-rating__disc {
        background: var(--sr-star);
        transform: scale(1.06);
        box-shadow: 0 6px 16px -6px var(--sr-star);
    }
    .star-rating__option.is-peak .star-rating__glyph {
        fill: #fff;
        transform: scale(1.08);
    }
    .star-rating__option.is-peak .star-rating__num {
        color: var(--sr-star-deep);
        font-weight: 700;
    }
    .star-rating__option.is-selected.is-peak .star-rating__disc {
        animation: star-settle .32s var(--sr-ease);
    }

    .star-rating__caption {
        min-height: 22px;
        margin-top: 8px;
        text-align: center;
        font-size: 13px;
        font-weight: 700;
        color: var(--sr-star-deep);
    }

    .star-rating-fade-enter-active, .star-rating-fade-leave-active { transition: opacity .18s var(--sr-ease); }
    .star-rating-fade-enter, .star-rating-fade-leave-to { opacity: 0; }
    .star-rating-fade-leave-active { position: absolute; }
    .star-rating__caption { position: relative; }

    .star-rating.is-disabled .star-rating__option { cursor: default; }
    .star-rating.is-disabled .star-rating__option:not(.is-lit) { opacity: .6; }

    /* Compact variant (builder preview) */
    .star-rating.is-compact {
        --sr-disc: 52px;
        --sr-glyph: 28px;
        max-width: 520px;
        margin: 0;
    }
    .star-rating.is-compact .star-rating__option { padding: 10px 4px 8px; gap: 6px; }
    .star-rating.is-compact .star-rating__caption { min-height: 18px; margin-top: 6px; font-size: 12px; }

    @keyframes star-settle {
        0%   { transform: scale(.92); }
        60%  { transform: scale(1.12); }
        100% { transform: scale(1.06); }
    }

    @media (max-width: 600px) {
        .star-rating { --sr-disc: 48px; --sr-glyph: 26px; max-width: 100%; }
        .star-rating__row { gap: 2px; padding: 6px; }
        .star-rating__option { padding: 10px 2px 8px; gap: 6px; }
        .star-rating__num { font-size: 11px; }
    }

    @media (prefers-reduced-motion: reduce) {
        .star-rating__option, .star-rating__disc, .star-rating__glyph, .star-rating__num { transition: none; }
        .star-rating__option.is-selected.is-peak .star-rating__disc { animation: none; }
        .star-rating-fade-enter-active, .star-rating-fade-leave-active { transition: none; }
    }
</style>
